// src/pages/PainelCliente/PainelCliente.js
// Estrutura do painel do cliente: menu superior (desktop), barra inferior
// (celular), menu do usuário, Central de ajuda e tour guiado.

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import axios, { BASE_URL } from "../../axiosConfig";
import { CarrinhoContext } from "../../CarrinhoContext";
import ModalAtualizacaoPerfil from "../../components/ModalAtualizacaoPerfil";
import TourGuiado from "../../components/ajuda/TourGuiado";
import CentralAjuda from "../../components/ajuda/CentralAjuda";
import { ROTAS, TOURS } from "../../components/ajuda/ajudaConteudo";
import { formatarPontos, lerUsuario, primeiroNome } from "../../utils/tikpontos";

import "../../styles/tikpontos-theme.css";
import "../../styles/tikpontos-ajustes-paginas.css";
import "./PainelCliente.css";

import {
  BadgeDollarSign,
  CalendarCheck,
  ChevronDown,
  Coins,
  FileEdit,
  FileText,
  Gift,
  HelpCircle,
  Home,
  LogOut,
  Menu,
  Package,
  Search,
  ShoppingCart,
  Sparkles,
  Star,
  UserRound,
  X,
} from "lucide-react";

const NAV_PRINCIPAL = [
  { to: ROTAS.home, label: "Início", end: true },
  { to: ROTAS.catalogo, label: "Prêmios" },
  { to: ROTAS.pontos, label: "Meus pontos" },
  { to: ROTAS.resgates, label: "Meus resgates" },
  { to: ROTAS.campanhas, label: "Campanhas" },
  { to: ROTAS.pix, label: "Pix Premiado", className: "is-pix" },
  { to: ROTAS.experience, label: "Experience", className: "is-experience" },
];

const NAV_INFERIOR = [
  { to: ROTAS.home, label: "Início", icon: Home, end: true },
  { to: ROTAS.catalogo, label: "Prêmios", icon: Gift },
  { to: ROTAS.pontos, label: "Pontos", icon: Coins },
  { to: ROTAS.resgates, label: "Resgates", icon: Package },
];

const MENU_MAIS = [
  { to: ROTAS.campanhas, label: "Campanhas", desc: "Metas e recompensas extras", icon: Star },
  { to: ROTAS.experience, label: "Tikpag Experience", desc: "PINs e experiências", icon: Sparkles },
  { to: ROTAS.pix, label: "Pix Premiado", desc: "Solicite e acompanhe", icon: BadgeDollarSign },
  { to: ROTAS.operacao, label: "Cadastrar operação", desc: "Envie para ganhar pontos", icon: FileEdit },
  { to: ROTAS.eventos, label: "Eventos", desc: "Eventos e sorteios", icon: CalendarCheck },
  { to: ROTAS.carrinho, label: "Carrinho", desc: "Finalize seus resgates", icon: ShoppingCart },
  { to: ROTAS.perfil, label: "Meu perfil", desc: "Seus dados cadastrais", icon: UserRound },
  { to: ROTAS.regras, label: "Regras do programa", desc: "Como o TikPontos funciona", icon: FileText },
];

const ROTAS_DO_MAIS = MENU_MAIS.map((m) => m.to);

const chaveTourVisto = (id) => `tk_tour_visto_${id || "anon"}`;

function normalizarRota(pathname) {
  return pathname.replace(/\/+$/, "") || "/";
}

function PainelCliente() {
  const navigate = useNavigate();
  const location = useLocation();
  const rotaAtual = normalizarRota(location.pathname);

  const { carrinho } = useContext(CarrinhoContext);
  const usuario = useMemo(() => lerUsuario() || {}, []);

  const precisaAtualizarPerfil =
    usuario?.tipo === "cliente" &&
    (usuario?.forcar_atualizacao_perfil === true ||
      usuario?.perfil_atualizado === false ||
      usuario?.documento_validado === false ||
      usuario?.termo_pontos_aceito === false);

  const [saldo, setSaldo] = useState(null);
  const [maisAberto, setMaisAberto] = useState(false);
  const [usuarioAberto, setUsuarioAberto] = useState(false);
  const [centralAberta, setCentralAberta] = useState(false);
  const [tourRota, setTourRota] = useState(null);
  const [tourPendente, setTourPendente] = useState(null);

  const menuUsuarioRef = useRef(null);

  const qtdCarrinho = Array.isArray(carrinho)
    ? carrinho.reduce((acc, item) => acc + Number(item.quantidade || 1), 0)
    : 0;

  // Saldo no topo: atualiza a cada troca de página (muda após um resgate)
  useEffect(() => {
    if (!usuario?.id) return;
    let ativo = true;
    axios
      .get(`/clientes/${usuario.id}/pontos`)
      .then((res) => {
        if (ativo) setSaldo(Number(res.data?.pontos_disponiveis ?? 0));
      })
      .catch(() => {});
    return () => {
      ativo = false;
    };
  }, [usuario?.id, rotaAtual]);

  // Fecha menus ao trocar de página
  useEffect(() => {
    setMaisAberto(false);
    setUsuarioAberto(false);
  }, [rotaAtual]);

  // Fecha o menu do usuário ao clicar fora ou apertar Esc
  useEffect(() => {
    if (!usuarioAberto) return;
    const fora = (e) => {
      if (menuUsuarioRef.current && !menuUsuarioRef.current.contains(e.target)) {
        setUsuarioAberto(false);
      }
    };
    const esc = (e) => e.key === "Escape" && setUsuarioAberto(false);
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
    };
  }, [usuarioAberto]);

  // Trava a rolagem com o menu "Mais" aberto
  useEffect(() => {
    if (!maisAberto) return;
    document.body.style.overflow = "hidden";
    const esc = (e) => e.key === "Escape" && setMaisAberto(false);
    document.addEventListener("keydown", esc);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", esc);
    };
  }, [maisAberto]);

  // ---------- Tour ----------

  const iniciarTour = useCallback(
    (rota) => {
      setCentralAberta(false);
      setMaisAberto(false);
      setUsuarioAberto(false);
      if (!TOURS[rota]) return;
      if (rota !== rotaAtual) {
        setTourPendente(rota);
        navigate(rota);
      } else {
        setTimeout(() => setTourRota(rota), 250);
      }
    },
    [navigate, rotaAtual]
  );

  // Começa o tour depois que a nova página carregou
  useEffect(() => {
    if (!tourPendente || tourPendente !== rotaAtual) return;
    const t = setTimeout(() => {
      setTourRota(tourPendente);
      setTourPendente(null);
    }, 900);
    return () => clearTimeout(t);
  }, [tourPendente, rotaAtual]);

  // Primeiro acesso: tour geral automático na Home
  useEffect(() => {
    if (rotaAtual !== ROTAS.home || precisaAtualizarPerfil || !usuario?.id) return;
    if (localStorage.getItem(chaveTourVisto(usuario.id))) return;
    const t = setTimeout(() => setTourRota(ROTAS.home), 1500);
    return () => clearTimeout(t);
  }, [rotaAtual, precisaAtualizarPerfil, usuario?.id]);

  const fecharTour = useCallback(() => {
    if (tourRota === ROTAS.home && usuario?.id) {
      localStorage.setItem(chaveTourVisto(usuario.id), "1");
    }
    setTourRota(null);
  }, [tourRota, usuario?.id]);

  const fecharCentral = useCallback(() => setCentralAberta(false), []);

  const irPara = useCallback(
    (rota) => {
      setCentralAberta(false);
      navigate(rota);
    },
    [navigate]
  );

  const handleLogout = () => {
    localStorage.removeItem("usuario");
    localStorage.removeItem("token");
    navigate("/login", { replace: true });
  };

  const inicial = usuario?.nome?.charAt(0)?.toUpperCase() || "U";
  const maisAtivo = ROTAS_DO_MAIS.some((r) => rotaAtual.startsWith(r));

  return (
    <div className="tk-app tk-painel">
      <a href="#tk-conteudo" className="tk-pular-conteudo">
        Pular para o conteúdo
      </a>

      <header className="tk-topo">
        <div className="tk-topo-inner">
          <Link to={ROTAS.home} className="tk-marca" aria-label="TikPontos, ir para o início">
            <img src={`${BASE_URL}/uploads/tikpontos.png`} alt="TikPontos" />
          </Link>

          <nav className="tk-nav" aria-label="Menu principal" data-tour="menu-desktop">
            {NAV_PRINCIPAL.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `tk-nav-link ${item.className || ""} ${isActive ? "is-ativo" : ""}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="tk-topo-acoes">
            <Link to={ROTAS.pontos} className="tk-saldo-chip" title="Pontos disponíveis">
              <strong>{saldo === null ? "…" : formatarPontos(saldo)}</strong>
              <span>pts</span>
            </Link>

            <button
              type="button"
              className="tk-ajuda-btn"
              data-tour="ajuda"
              onClick={() => setCentralAberta(true)}
              aria-label="Ajuda: busque uma dúvida ou faça o tour"
            >
              <Search size={18} aria-hidden="true" />
              <span className="tk-ajuda-texto">Tem dúvida? Pergunte aqui</span>
            </button>

            <Link
              to={ROTAS.carrinho}
              className="tk-carrinho"
              data-tour="carrinho"
              aria-label={`Carrinho, ${qtdCarrinho} ${qtdCarrinho === 1 ? "item" : "itens"}`}
            >
              <ShoppingCart size={22} aria-hidden="true" />
              {qtdCarrinho > 0 && <span className="tk-badge">{qtdCarrinho > 99 ? "99+" : qtdCarrinho}</span>}
            </Link>

            <div className="tk-usuario" ref={menuUsuarioRef}>
              <button
                type="button"
                className="tk-usuario-btn"
                aria-haspopup="menu"
                aria-expanded={usuarioAberto}
                onClick={() => setUsuarioAberto((v) => !v)}
              >
                <span className="tk-avatar" aria-hidden="true">{inicial}</span>
                <span className="tk-usuario-nome">{primeiroNome(usuario?.nome)}</span>
                <ChevronDown size={16} aria-hidden="true" />
              </button>

              {usuarioAberto && (
                <div className="tk-usuario-menu" role="menu">
                  <div className="tk-usuario-info">
                    <strong>{usuario?.nome}</strong>
                    <span>{usuario?.email}</span>
                  </div>
                  <Link role="menuitem" to={ROTAS.perfil}>
                    <UserRound size={17} aria-hidden="true" /> Meu perfil
                  </Link>
                  <Link role="menuitem" to={ROTAS.operacao}>
                    <FileEdit size={17} aria-hidden="true" /> Cadastrar operação
                  </Link>
                  <Link role="menuitem" to={ROTAS.eventos}>
                    <CalendarCheck size={17} aria-hidden="true" /> Eventos
                  </Link>
                  <Link role="menuitem" to={ROTAS.regras}>
                    <FileText size={17} aria-hidden="true" /> Regras do programa
                  </Link>
                  <button role="menuitem" type="button" onClick={() => { setUsuarioAberto(false); setCentralAberta(true); }}>
                    <HelpCircle size={17} aria-hidden="true" /> Ajuda e tour
                  </button>
                  <div className="tk-usuario-divisor" />
                  <button role="menuitem" type="button" className="is-sair" onClick={handleLogout}>
                    <LogOut size={17} aria-hidden="true" /> Sair
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main id="tk-conteudo" className="tk-conteudo">
        <Outlet />
      </main>

      {/* Barra inferior: só no celular */}
      <nav className="tk-barra-inferior" aria-label="Navegação principal" data-tour="menu-mobile">
        {NAV_INFERIOR.map(({ to, label, icon: Icone, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `tk-barra-item ${isActive ? "is-ativo" : ""}`}
          >
            <Icone size={22} aria-hidden="true" />
            <span>{label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={`tk-barra-item ${maisAtivo || maisAberto ? "is-ativo" : ""}`}
          aria-expanded={maisAberto}
          onClick={() => setMaisAberto(true)}
        >
          <Menu size={22} aria-hidden="true" />
          <span>Mais</span>
        </button>
      </nav>

      {maisAberto && (
        <div className="tk-mais">
          <button
            type="button"
            className="tk-mais-fundo"
            aria-label="Fechar menu"
            onClick={() => setMaisAberto(false)}
          />
          <div className="tk-mais-painel" role="dialog" aria-modal="true" aria-label="Mais opções">
            <div className="tk-mais-topo">
              <span className="tk-avatar tk-avatar--grande" aria-hidden="true">{inicial}</span>
              <div className="tk-mais-usuario">
                <strong>{usuario?.nome}</strong>
                <span>{usuario?.email}</span>
              </div>
              <button
                type="button"
                className="tk-mais-fechar"
                aria-label="Fechar"
                onClick={() => setMaisAberto(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="tk-mais-lista">
              {MENU_MAIS.map(({ to, label, desc, icon: Icone }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) => `tk-mais-item ${isActive ? "is-ativo" : ""}`}
                >
                  <span className="tk-mais-icone"><Icone size={20} aria-hidden="true" /></span>
                  <span className="tk-mais-textos">
                    <strong>{label}</strong>
                    <span>{desc}</span>
                  </span>
                </NavLink>
              ))}
              <button
                type="button"
                className="tk-mais-item"
                onClick={() => {
                  setMaisAberto(false);
                  setCentralAberta(true);
                }}
              >
                <span className="tk-mais-icone"><HelpCircle size={20} aria-hidden="true" /></span>
                <span className="tk-mais-textos">
                  <strong>Ajuda e tour</strong>
                  <span>Busque uma dúvida ou refaça o tour</span>
                </span>
              </button>
            </div>

            <button type="button" className="tk-btn tk-btn--secundario tk-btn--bloco tk-mais-sair" onClick={handleLogout}>
              <LogOut size={18} aria-hidden="true" /> Sair
            </button>
          </div>
        </div>
      )}

      {precisaAtualizarPerfil && rotaAtual !== ROTAS.perfil && (
        <ModalAtualizacaoPerfil onAtualizar={() => navigate(ROTAS.perfil)} />
      )}

      <CentralAjuda
        aberto={centralAberta}
        onFechar={fecharCentral}
        rotaAtual={rotaAtual}
        onIrPara={irPara}
        onIniciarTour={iniciarTour}
      />

      <TourGuiado passos={tourRota ? TOURS[tourRota] : null} aberto={Boolean(tourRota)} onFechar={fecharTour} />
    </div>
  );
}

export default PainelCliente;
